import CustomLoading from '@/components/custom-loading';
import { Card } from '@/components/ui/card';
import { useCrmCompanyQuery, useGiftCardCheckCodeQuery } from '@/gql/generated';
import GiftVoucherIcon from '@/icons/gift-voucher-icon';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import CheckGiftCard from '../_components/check-gift-card';
import GiftCardDetail from '../_components/gift-card-detail';
import NoData from '../_components/no-data';
import TableDesktop from '../_components/table-desktop';
import TableMobile from '../_components/table-mobile';

type TransactionType = {
  dts_order_id?: number | null;
  balance_after?: number | null;
  balance_before?: number | null;
  balance_change?: number | null;
  type?: string | null;
  date?: string | null;
};

const ErrorState = () => (
  <div className="flex items-center justify-center px-4">
    <div className="flex flex-col items-center">
      <GiftVoucherIcon className="size-40" />
      <div>
        <h3 className="text-lg font-semibold text-gray-900 text-center">
          Gift Card Not Found
        </h3>
        <p className="text-sm text-center">
          The gift card you're looking for could not be found. Please check the
          code and try again.
        </p>
      </div>
    </div>
  </div>
);

const TransactionHistory = ({
  transactions,
  timeZone,
}: {
  transactions: TransactionType[];
  timeZone: string;
}) => {
  if (!transactions?.length) return <NoData />;

  return (
    <Card className="mx-auto mb-4 p-4 transition-all hover:border-blue-200 md:mb-6 md:p-6">
      <div className="mb-4 text-lg font-bold sm:text-xl">
        Transaction History
      </div>
      <div className="hidden md:block">
        <TableDesktop transactions={transactions} timeZone={timeZone} />
      </div>
      <div className="block md:hidden">
        <TableMobile transactions={transactions} timeZone={timeZone} />
      </div>
    </Card>
  );
};

const CheckGiftCardPage = () => {
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');
  const { whmcs_id: whmcsId } = useParams();
  const navigate = useNavigate();

  const crmCompanyQuery = useCrmCompanyQuery({
    input_obj: { whmcs_id: Number(whmcsId) },
  });

  const giftCardCheckCodeQuery = useGiftCardCheckCodeQuery(
    {
      input_obj: {
        code: code ?? '',
        whmcs_id: Number(whmcsId),
      },
    },
    {
      enabled: !!code,
    },
  );

  const timeZone = crmCompanyQuery.data?.crm_company?.time_zone;
  const giftCardData = giftCardCheckCodeQuery.data?.gift_card_check_code;
  const transactions = giftCardData?.transactions ?? [];

  const handleCheck = (cardCode: string) => {
    navigate(`/${whmcsId}?code=${cardCode.replace(/[\s-]/g, '')}`);
    giftCardCheckCodeQuery.refetch();
  };

  return (
    <div className="size-full overflow-auto max-w-4xl mx-auto p-4">
      <div>
        <CheckGiftCard
          giftCardCode={code ?? ''}
          onCheck={handleCheck}
          whmcsId={Number(whmcsId)}
        />
      </div>

      {giftCardCheckCodeQuery.isFetching && (
        <div className="fixed left-0 top-0 size-full bg-slate-100/80">
          <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
            <CustomLoading />
          </div>
        </div>
      )}

      {giftCardCheckCodeQuery.isError &&
        code &&
        crmCompanyQuery.data?.crm_company && <ErrorState />}

      {giftCardCheckCodeQuery.isFetched && giftCardData && code && (
        <>
          <GiftCardDetail
            giftCardDetail={giftCardData}
            code={code}
            timeZone={timeZone!}
          />
          <TransactionHistory
            transactions={transactions}
            timeZone={timeZone!}
          />
        </>
      )}
    </div>
  );
};

export default CheckGiftCardPage;
